import { useLocation, useNavigate } from 'react-router-dom'

import { Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  ProjectsDataTableDocument,
  ProjectsDataTableQuery,
} from 'generated/resources/datatable.graphql'
import {
  BulkAssignProjectWorkerDocument,
  BulkChangeAssignmentDocument,
  ProjectManagementProjectEmployeesAssignmentEnum,
  ProjectManagementProjectsListPageAssignEmployeesModalPageDocument,
} from 'generated/resources/graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LoadingState } from 'modules/project_management/components/LoadingState'
import AssignEmployeesModal from 'modules/project_management/components/modals/AssignEmployeesModal'

// TODO: remove this page once we have the new data collections enabled in prod
const AssignEmployeesModalPage = () => {
  const { company } = useCurrentSession()
  const { state } = useLocation()
  const { hasBulkActions } = state || { hasBulkActions: false }
  const navigate = useNavigate()

  const [bulkAssign, { loading: loadingBulkAssign }] = useMutation(
    BulkAssignProjectWorkerDocument,
    {
      extractorFn: (data) =>
        data.projectManagementMutations.bulkAssignProjectmanagementProjectWorker,
      refetchQueries: [ProjectsDataTableDocument],
      awaitRefetchQueries: true,
    }
  )
  const [bulkChangeAssignment, { loading: loadingBulkChangeAssignment }] = useMutation(
    BulkChangeAssignmentDocument,
    {
      extractorFn: (data) =>
        data.projectManagementMutations.bulkChangeAssignmentProjectmanagementProject,
      refetchQueries: [ProjectsDataTableDocument],
      awaitRefetchQueries: true,
    }
  )

  const dataTable = useDataTable<ProjectsDataTableQuery>()
  const selectedProjectIds = Array.from(dataTable.selectedRows).map((item) => +item)

  const multipleSelection = selectedProjectIds.length > 1

  const initialEmployeesAssignment = multipleSelection
    ? undefined
    : dataTable.data?.find((el) => el['id'] === selectedProjectIds[0]).employeesAssignment

  const { loading: loadingQuery, data } = useQuery(
    ProjectManagementProjectsListPageAssignEmployeesModalPageDocument,
    {
      variables: {
        projectIds: selectedProjectIds,
        companyId: company.id,
        featureIds: [Features.projects_overview],
      },
      skip: initialEmployeesAssignment === 'company',
    }
  )

  const projects = data ? data.projectManagement.projectsConnection.nodes : []

  const hasNotificationsOnAssignation =
    !!data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.projects_overview
    )?.available

  const canAssignEmployees = dataTable.data
    .filter((item) => selectedProjectIds.includes(item.id))
    .map((item) => item._policies_assignUnassignProjectWorkers.can)
    .every((can) => can)

  if (!hasBulkActions || !canAssignEmployees) {
    return null
  }

  if (loadingQuery) return <LoadingState />

  const initialAssignedEmployeeIds = () => {
    if (multipleSelection) {
      return []
    }

    if (projects[0]?.employeesAssignment !== 'manual') {
      return []
    }

    // used flatMap instead of map for type safety; in order to avoid undefined if no pw.employee
    // (that should always come as it is asked, but typescript does not know)
    const assignedEmployees = projects[0]?.projectWorkers.flatMap((pw) => {
      return pw.employee && pw.assigned
        ? [
            {
              id: pw.employee.id,
              firstName: pw.employee.firstName,
              fullName: pw.employee.fullName,
              lastName: pw.employee.lastName,
              access: {
                id: pw.employee.access.id,
              },
            },
          ]
        : []
    })

    return assignedEmployees || []
  }

  const totalSelectedProjectWorkers = dataTable.data
    .filter((item) => selectedProjectIds.includes(item.id))
    .reduce((acc, item) => acc + item.projectworkersConnection_totalCount, 0)

  const onClose = () => {
    navigate(-1)
  }

  const onSubmit = async (
    _initialEmployeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum | undefined,
    newEmployeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum,
    selectedEmployees: number[]
  ) => {
    const responseChangeAssignment =
      multipleSelection || _initialEmployeesAssignment !== newEmployeesAssignment
        ? await bulkChangeAssignment({
            variables: {
              ids: selectedProjectIds,
              employeesAssignment: newEmployeesAssignment,
            },
          })
        : undefined

    const responseAssign =
      newEmployeesAssignment === 'manual'
        ? await bulkAssign({
            variables: {
              employeeIds: selectedEmployees,
              projectIds: selectedProjectIds,
              notifyEmployees: hasNotificationsOnAssignation,
            },
          })
        : undefined

    let handled = undefined
    if (responseAssign) {
      handled = handleResult(responseAssign, () => undefined)
    } else {
      if (responseChangeAssignment)
        handled = handleResult(responseChangeAssignment, () => undefined)
    }

    if (handled && handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.toasts.assign_employees_multiple_projects_success'),
      })

      track(trackingEvents.projectManagement.bulkActions.employeeProjectsAssignment.eventSubmit, {
        projects_employee_assignment_selection: newEmployeesAssignment,
        projects_assigned_count: selectedProjectIds.length,
        notify_employees: hasNotificationsOnAssignation,
      })
    }

    if (handled && !handled.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }

    onClose()
  }

  return (
    <AssignEmployeesModal
      iconName={'add-user'}
      title={i18n.t('project_management.modals.assign_employees.title')}
      description={i18n.tView('project_management.modals.assign_employees.groupDescription', {
        n_projects: <Text size="300">{selectedProjectIds.length}</Text>,
      })}
      loading={loadingBulkAssign || loadingBulkChangeAssignment}
      showWarning={multipleSelection && !!totalSelectedProjectWorkers}
      submitButton={i18n.t('project_management.modals.assign_employees.submit')}
      employeesAssignment={initialEmployeesAssignment}
      initialAssignedEmployees={initialAssignedEmployeeIds()}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}

export default AssignEmployeesModalPage
